import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Button, Row, Col, Statistic, Tag, message, Spin, Drawer, Divider, Space, DatePicker, Select, Empty, Modal, Form, Input, InputNumber, Alert } from 'antd';
import { DollarOutlined, UserOutlined, EyeOutlined, EditOutlined, DeleteOutlined, PlusOutlined, ThunderboltOutlined, DownloadOutlined, BankOutlined, SendOutlined } from '@ant-design/icons';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip, ResponsiveContainer } from 'recharts';
import dayjs from 'dayjs';
import apiService from "services/api/apiService";
import { feedbackSuccess, feedbackError } from "../../utils/feedback";

interface PayrollRecord {
  id: number;
  employee_id: number;
  first_name: string;
  last_name: string;
  position: string;
  department: string;
  pay_period: string;
  basic_salary: number;
  allowances: number;
  overtime_pay: number;
  bonus: number;
  gross_pay: number;
  paye_tax: number;
  pension_employee: number;
  nhis_deduction: number;
  other_deductions: number;
  total_deductions: number;
  net_pay: number;
  payment_date: string;
  status: 'draft' | 'approved' | 'paid' | 'processed';
  paystack_recipient_code?: string;
  bank_name?: string;
  account_number?: string;
  account_name?: string;
  currency_code?: string;
}

interface PayrollSummary {
  total_employees: number;
  total_basic: number;
  total_gross: number;
  total_deductions: number;
  total_net_pay: number;
  total_paye: number;
  total_pension: number;
  total_nhis: number;
  paid_count: number;
  approved_count: number;
  draft_count: number;
  processed_count: number;
}

interface DepartmentSummary {
  department: string;
  employee_count: number;
  total_gross: number;
  total_net_pay: number;
}

const fmt = (v: any) => `₦${parseFloat(String(v || 0)).toLocaleString('en-NG', { minimumFractionDigits: 2 })}`;

const PayrollDashboard: React.FC = () => {
  const [records, setRecords]                   = useState<PayrollRecord[]>([]);
  const [summary, setSummary]                   = useState<PayrollSummary | null>(null);
  const [loading, setLoading]                   = useState(false);
  const [calculating, setCalculating]           = useState(false);
  const [month, setMonth]                       = useState(dayjs().format('YYYY-MM'));
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord]     = useState<PayrollRecord | null>(null);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [detailDrawer, setDetailDrawer]         = useState(false);
  const [selectedDetail, setSelectedDetail]     = useState<PayrollRecord | null>(null);
  const [departmentSummary, setDepartmentSummary] = useState<DepartmentSummary[]>([]);
  const [employees, setEmployees]               = useState<any[]>([]);
  const [form]                                  = Form.useForm();
  const deductionModeRef = React.useRef<'amount' | 'percent'>('amount');
  const [bankModal, setBankModal] = useState(false);
  const [bankEmployee, setBankEmployee] = useState<any>(null);
  const [banks, setBanks] = useState<any[]>([]);
  const [bankForm] = Form.useForm();
  const [verifying, setVerifying] = useState(false);
  const [manualPayModal, setManualPayModal] = useState(false);
  const [manualPayRecord, setManualPayRecord] = useState<any>(null);
  const [manualPayForm] = Form.useForm();
  const [paymentMethod, setPaymentMethod] = useState('paystack');

  // Load payment method setting
  React.useEffect(() => {
    const tid = localStorage.getItem('hr_tenant_id') || 'superadmin';
    fetch(`https://nexus-hr.talenthubupskilling.com/backend/hr-api/settings/settings.php?tenant_id=${tid}&action=system_config`)
      .then(r => r.json()).then(d => {
        if (d.success) setPaymentMethod(d.data?.payment_method || 'paystack');
      }).catch(() => {});
  }, []);
  const [bankVerified, setBankVerified] = useState<any>(null);
  const [recipientCode, setRecipientCode] = useState<string>('');
  const [transferring, setTransferring] = useState(false);
  const [bulkTransferring, setBulkTransferring] = useState(false);
  
  useEffect(() => { fetchData(); fetchEmployees(); }, [month]);

  const fetchEmployees = async () => {
    try {
      const r = await apiService.getEmployees();
      if (r.success) setEmployees(r.data || []);
    } catch {}
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [rr, sr, dr] = await Promise.all([
        apiService.getPayrollRecords({ month }),
        apiService.getPayrollSummary(month),
        apiService.getDepartmentPayrollSummary(month),
      ]);
      if (rr.success) setRecords(rr.data || []);
      if (sr.success) setSummary(sr.data);
      if (dr.success) setDepartmentSummary(dr.data || []);
    } catch { message.error('Failed to fetch payroll data'); }
    finally { setLoading(false); }
  };

  // Auto-calculate PAYE/Pension/NHIS from backend
  const autoCalculate = useCallback(async (formInstance: any) => {
    const vals = formInstance.getFieldsValue();
    const basic = parseFloat(vals.basic_salary) || 0;
    if (!basic) { message.warning('Enter basic salary first'); return; }
    setCalculating(true);
    try {
      const res = await apiService.calculatePayroll(
        basic,
        Number(vals.allowances) || 0,
        Number(vals.overtime_pay) || 0,
        Number(vals.bonus) || 0
      );
      if (res.success) {
        formInstance.setFieldsValue({
          gross_pay:        res.data.gross_pay,
          paye_tax:         res.data.paye_tax,
          pension_employee: res.data.pension_employee,
          nhis_deduction:   res.data.nhis_deduction,
          total_deductions: res.data.total_deductions,
          net_pay:          res.data.net_pay,
        });
        message.success('Calculated using Nigerian tax rates (PAYE, Pension 8%, NHIS 1.75%)');
      }
    } catch { message.error('Auto-calculate failed'); }
    finally { setCalculating(false); }
  }, []);

  const handleEdit = (record: PayrollRecord) => {
    setSelectedRecord(record);
    deductionModeRef.current = 'amount';
    form.setFieldsValue({ ...record });
    setEditModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(`Delete payroll record #${id}? This cannot be undone.`)) return;
    try {
      const r = await apiService.deletePayrollRecord(id);
      if (r.success) { feedbackSuccess('Payroll record deleted'); fetchData(); }
      else message.error(r.error || 'Failed to delete');
    } catch { message.error('Failed to delete'); }
  };

  const handleCreate = () => {
    form.resetFields();
    deductionModeRef.current = 'amount';
    form.setFieldsValue({ pay_period: dayjs().format('MMMM YYYY'), status: 'draft' });
    setCreateModalVisible(true);
  };

  const buildPayload = (values: any) => {
    const basic      = Number(values.basic_salary) || 0;
    const allowances = Number(values.allowances) || 0;
    const overtime   = Number(values.overtime_pay) || 0;
    const bonus      = Number(values.bonus) || 0;
    const gross      = basic + allowances + overtime + bonus;
    // Use ref for deduction mode — form values don't persist non-Form.Item fields reliably
    const isPercent  = deductionModeRef.current === 'percent';
    const resolve    = (v: number) => isPercent ? (gross * v) / 100 : v;
    const paye       = resolve(Number(values.paye_tax) || 0);
    const pension    = resolve(Number(values.pension_employee) || 0);
    const nhis       = resolve(Number(values.nhis_deduction) || 0);
    const other      = resolve(Number(values.other_deductions) || 0);
    const deductions = paye + pension + nhis + other;
    const net        = gross - deductions;
    return { ...values, basic_salary: basic, allowances, overtime_pay: overtime, bonus, gross_pay: gross, paye_tax: parseFloat(paye.toFixed(2)), pension_employee: parseFloat(pension.toFixed(2)), nhis_deduction: parseFloat(nhis.toFixed(2)), other_deductions: parseFloat(other.toFixed(2)), total_deductions: parseFloat(deductions.toFixed(2)), net_pay: parseFloat(net.toFixed(2)), currency_code: 'NGN', payment_date: dayjs().format('YYYY-MM-DD') };
  };

  const handleCreateSubmit = async (values: any) => {
    try {
      const r = await apiService.createPayrollRecord(buildPayload(values));
      if (r.success) { feedbackSuccess('Payroll record created'); setCreateModalVisible(false); form.resetFields(); fetchData(); }
      else message.error(r.error || 'Failed to create');
    } catch { message.error('Failed to create'); }
  };

  const handleEditSubmit = async (values: any) => {
    if (!selectedRecord) return;
    try {
      const r = await apiService.updatePayrollRecord(selectedRecord.id, buildPayload(values));
      if (r.success) { message.success('Updated'); setEditModalVisible(false); setSelectedRecord(null); form.resetFields(); fetchData(); }
      else message.error(r.error || 'Failed to update');
    } catch { message.error('Failed to update'); }
  };

  const columns = [
    { title: 'Employee', key: 'employee', render: (_: any, r: PayrollRecord) => `${r.first_name || 'N/A'} ${r.last_name || ''}`, width: 150 },
    { title: 'Department', dataIndex: 'department', width: 120 },
    { title: 'Pay Period', dataIndex: 'pay_period', width: 130 },
    { title: 'Basic Salary', dataIndex: 'basic_salary', render: fmt, align: 'right' as const, width: 130 },
    { title: 'Gross Pay', dataIndex: 'gross_pay', render: fmt, align: 'right' as const, width: 120 },
    { title: 'Deductions', dataIndex: 'total_deductions', render: fmt, align: 'right' as const, width: 120 },
    { title: 'Net Pay', dataIndex: 'net_pay', render: (v: any) => <span style={{ color: '#1890ff', fontWeight: 'bold' }}>{fmt(v)}</span>, align: 'right' as const, width: 130 },
    { title: 'Status', dataIndex: 'status', render: (s: string) => <Tag color={s==='paid'||s==='processed'?'success':s==='approved'?'processing':'warning'}>{s?.toUpperCase()}</Tag>, width: 100 },
    { title: 'Actions', render: (_: any, r: PayrollRecord) => (
      <Space>
        <Button type="text" size="small" icon={<EyeOutlined />} onClick={() => { setSelectedDetail(r); setDetailDrawer(true); }} />
        <Button type="text" size="small" icon={<EditOutlined />} onClick={() => handleEdit(r)} />
        <Button type="text" size="small" icon={<DownloadOutlined />} title="Download Payslip PDF"
          onClick={async () => { message.loading('Generating payslip...', 1); await apiService.downloadPayslip(Number(r.id)); }} />
        {(r.status === 'approved' || r.status === 'paid') && (
          <Button type="text" size="small" icon={<SendOutlined />}
            title={paymentMethod === 'paystack' ? (r.paystack_recipient_code ? 'Initiate Transfer' : 'Set Up Bank Transfer') : 'Record Payment'}
            style={{ color: paymentMethod !== 'paystack' ? '#1890ff' : (r.paystack_recipient_code ? '#52c41a' : '#fa8c16') }}
            loading={transferring}
            onClick={async (e) => {
              e.stopPropagation();
              // Always fetch fresh payment method on click
              let currentMethod = paymentMethod;
              try {
                const cfg = await fetch(`https://nexus-hr.talenthubupskilling.com/backend/hr-api/settings/settings.php?tenant_id=${apiService.getTenantId()}&action=system_config`).then(r=>r.json());
                if (cfg?.success) { currentMethod = cfg.data?.payment_method || 'paystack'; setPaymentMethod(currentMethod); }
              } catch {}

              if (currentMethod === 'paystack') {
                if (r.paystack_recipient_code) {
                  if (!window.confirm(`Transfer ₦${Number(r.net_pay).toLocaleString()} to ${r.first_name} ${r.last_name}?

This will initiate a real Paystack transfer.`)) return;
                  setTransferring(true);
                  const res = await apiService.initiateTransfer(r.id);
                  setTransferring(false);
                  if (res.success || res.status) message.success('✅ Transfer initiated!');
                  else message.error(res.error || res.message || 'Transfer failed');
                } else {
                  setBankEmployee(r); setBankModal(true);
                  const banksRes = await apiService.getBanks();
                  if (banksRes.status) setBanks(banksRes.data || []);
                }
              } else {
                setManualPayRecord(r);
                setManualPayModal(true);
              }
            }} />
        )}
        <Button type="text" size="small" icon={<DeleteOutlined />} danger onClick={() => handleDelete(r.id)} />
      </Space>
    ), width: 120 },
  ];

  // Real deduction breakdown from summary
  const deductionPieData = summary ? [
    { name: 'PAYE Tax',        value: parseFloat(String(summary.total_paye    || 0)) },
    { name: 'Pension (8%)',    value: parseFloat(String(summary.total_pension  || 0)) },
    { name: 'NHIS (1.75%)',    value: parseFloat(String(summary.total_nhis    || 0)) },
  ].filter(d => d.value > 0) : [];

  const COLORS = ['#1890ff', '#52c41a', '#faad14'];

  const EarningsFields = ({ formRef }: { formRef: any }) => {
    const [deductionMode, setDeductionMode] = useState<'amount' | 'percent'>('amount');

    const resolveDeduction = (fieldName: string, gross: number) => {
      const val = Number(formRef.getFieldValue(fieldName)) || 0;
      return deductionMode === 'percent' ? (gross * val) / 100 : val;
    };

    const handleModeChange = (mode: 'amount' | 'percent') => {
      setDeductionMode(mode);
      deductionModeRef.current = mode;
      formRef.setFieldsValue({ paye_tax: 0, pension_employee: 0, nhis_deduction: 0, other_deductions: 0 });
    };

    return (
    <>
      <Divider>Earnings</Divider>
      <Row gutter={16}>
        <Col span={12}><Form.Item name="basic_salary" label="Basic Salary (₦)" rules={[{ required: true }]}><InputNumber min={0} style={{ width: '100%' }} formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} parser={(v: any) => v!.replace(/,/g, '')} /></Form.Item></Col>
        <Col span={12}><Form.Item name="allowances" label="Allowances (₦)"><InputNumber min={0} style={{ width: '100%' }} formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} parser={(v: any) => v!.replace(/,/g, '')} /></Form.Item></Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}><Form.Item name="overtime_pay" label="Overtime Pay (₦)"><InputNumber min={0} style={{ width: '100%' }} formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} parser={(v: any) => v!.replace(/,/g, '')} /></Form.Item></Col>
        <Col span={12}><Form.Item name="bonus" label="Bonus (₦)"><InputNumber min={0} style={{ width: '100%' }} formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} parser={(v: any) => v!.replace(/,/g, '')} /></Form.Item></Col>
      </Row>
      <Row gutter={16}>
        <Col span={24}>
          <Button icon={<ThunderboltOutlined />} loading={calculating} onClick={() => autoCalculate(formRef)} style={{ marginBottom: 16, background: '#f6ffed', borderColor: '#52c41a', color: '#52c41a' }}>
            Auto-Calculate PAYE, Pension & NHIS (Nigerian Rates)
          </Button>
        </Col>
      </Row>
      <Divider>
        Deductions &nbsp;
        <Select value={deductionMode} onChange={handleModeChange} size="small" style={{ width: 160 }}>
          <Select.Option value="amount">₦ Fixed Amount</Select.Option>
          <Select.Option value="percent">% of Gross Pay</Select.Option>
        </Select>
      </Divider>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="paye_tax" label={deductionMode === 'percent' ? 'PAYE Tax (%)' : 'PAYE Tax (₦)'}>
            <InputNumber min={0} max={deductionMode === 'percent' ? 100 : undefined} style={{ width: '100%' }}
              addonAfter={deductionMode === 'percent' ? '%' : '₦'}
              formatter={v => deductionMode === 'percent' ? `${v}` : `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(v: any) => v!.replace(/[,%₦]/g, '')} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="pension_employee" label={deductionMode === 'percent' ? 'Pension (%) — std 8%' : 'Pension Employee (₦)'}>
            <InputNumber min={0} max={deductionMode === 'percent' ? 100 : undefined} style={{ width: '100%' }}
              addonAfter={deductionMode === 'percent' ? '%' : '₦'}
              formatter={v => deductionMode === 'percent' ? `${v}` : `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(v: any) => v!.replace(/[,%₦]/g, '')} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="nhis_deduction" label={deductionMode === 'percent' ? 'NHIS (%) — std 1.75%' : 'NHIS (₦)'}>
            <InputNumber min={0} max={deductionMode === 'percent' ? 100 : undefined} style={{ width: '100%' }}
              addonAfter={deductionMode === 'percent' ? '%' : '₦'}
              formatter={v => deductionMode === 'percent' ? `${v}` : `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(v: any) => v!.replace(/[,%₦]/g, '')} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="other_deductions" label={deductionMode === 'percent' ? 'Other Deductions (%)' : 'Other Deductions (₦)'}>
            <InputNumber min={0} max={deductionMode === 'percent' ? 100 : undefined} style={{ width: '100%' }}
              addonAfter={deductionMode === 'percent' ? '%' : '₦'}
              formatter={v => deductionMode === 'percent' ? `${v}` : `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(v: any) => v!.replace(/[,%₦]/g, '')} />
          </Form.Item>
        </Col>
      </Row>
      {deductionMode === 'percent' && (
        <div style={{ background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 6, padding: '8px 12px', marginBottom: 16, fontSize: 13, color: '#856404' }}>
          💡 Percentage deductions will be calculated against gross pay when the record is saved.
        </div>
      )}
    </>
    );
  };

  return (
    <div style={{ padding: '24px' }}>
      <h1 style={{ marginBottom: '24px' }}>Payroll Management</h1>

      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}><Card loading={!summary}><Statistic title="Total Net Payroll" value={summary?.total_net_pay || 0} prefix="₦" precision={2} formatter={(v) => Number(v).toLocaleString('en-NG')} /></Card></Col>
        <Col xs={24} sm={12} lg={6}><Card loading={!summary}><Statistic title="Total Gross" value={summary?.total_gross || 0} prefix="₦" precision={2} formatter={(v) => Number(v).toLocaleString('en-NG')} /></Card></Col>
        <Col xs={24} sm={12} lg={6}><Card loading={!summary}><Statistic title="Total Deductions" value={summary?.total_deductions || 0} prefix="₦" precision={2} formatter={(v) => Number(v).toLocaleString('en-NG')} /></Card></Col>
        <Col xs={24} sm={12} lg={6}><Card loading={!summary}>
          <Space direction="vertical">
            <Tag color="success">{(summary?.paid_count||0)+(summary?.processed_count||0)} Paid/Processed</Tag>
            <Tag color="processing">{summary?.approved_count || 0} Approved</Tag>
            <Tag color="warning">{summary?.draft_count || 0} Draft</Tag>
          </Space>
        </Card></Col>
      </Row>

      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col xs={24} lg={12}>
          <Card title="Department Payroll">
            {departmentSummary.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={departmentSummary}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="department" />
                  <YAxis tickFormatter={(v) => `₦${(v/1000).toFixed(0)}k`} />
                  <ChartTooltip formatter={(v: any) => fmt(v)} />
                  <Bar dataKey="total_net_pay" fill="#1890ff" name="Net Pay" />
                </BarChart>
              </ResponsiveContainer>
            ) : <Empty description="No data for this month" />}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Deduction Breakdown (Real Data)">
            {deductionPieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={deductionPieData} cx="50%" cy="50%" outerRadius={100} dataKey="value" label={({ name, percent }) => `${name} ${((percent ?? 0) * 100).toFixed(0)}%`}>
                    {deductionPieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <ChartTooltip formatter={(v: any) => fmt(v)} />
                </PieChart>
              </ResponsiveContainer>
            ) : <Empty description="No deduction data for this month" />}
          </Card>
        </Col>
      </Row>

      <Card style={{ marginBottom: '24px' }}>
        <DatePicker picker="month" value={dayjs(month)} onChange={(d) => setMonth(d ? d.format('YYYY-MM') : dayjs().format('YYYY-MM'))} style={{ width: 200 }} />
      </Card>

      <Card title="Payroll Records" loading={loading} extra={
          <Space>
            <Button icon={<BankOutlined />} onClick={async () => {
              setBulkTransferring(true);
              const res = await apiService.initiateBulkTransfer(month);
              setBulkTransferring(false);
              if (res.success) { message.success(res.message); fetchData(); }
              else message.error(res.error || 'Bulk transfer failed');
            }} loading={bulkTransferring}>Bulk Transfer ({month})</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>Create New Payroll</Button>
          </Space>
        }>
        <Table columns={columns} dataSource={records} rowKey="id" pagination={{ pageSize: 10 }} scroll={{ x: 1100 }} />
      </Card>

      {/* Detail Drawer */}
      <Drawer title="Payroll Details" placement="right" onClose={() => setDetailDrawer(false)} open={detailDrawer} width={480}>
        {selectedDetail && (
          <div>
            <Divider>Employee</Divider>
            <p><strong>Name:</strong> {selectedDetail.first_name} {selectedDetail.last_name}</p>
            <p><strong>Department:</strong> {selectedDetail.department}</p>
            <p><strong>Pay Period:</strong> {selectedDetail.pay_period}</p>
            <Divider>Earnings</Divider>
            <Row gutter={16}>
              <Col span={12}><Statistic title="Basic Salary" value={selectedDetail.basic_salary} prefix="₦" precision={2} /></Col>
              <Col span={12}><Statistic title="Allowances" value={selectedDetail.allowances||0} prefix="₦" precision={2} /></Col>
            </Row>
            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col span={12}><Statistic title="Overtime" value={selectedDetail.overtime_pay||0} prefix="₦" precision={2} /></Col>
              <Col span={12}><Statistic title="Bonus" value={selectedDetail.bonus||0} prefix="₦" precision={2} /></Col>
            </Row>
            <Divider>Gross Pay</Divider>
            <Statistic title="Gross Pay" value={selectedDetail.gross_pay} prefix="₦" precision={2} valueStyle={{ color: '#1890ff' }} />
            <Divider>Deductions</Divider>
            <Row gutter={16}>
              <Col span={12}><Statistic title="PAYE Tax" value={selectedDetail.paye_tax||0} prefix="₦" precision={2} /></Col>
              <Col span={12}><Statistic title="Pension (8%)" value={selectedDetail.pension_employee||0} prefix="₦" precision={2} /></Col>
            </Row>
            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col span={12}><Statistic title="NHIS (1.75%)" value={selectedDetail.nhis_deduction||0} prefix="₦" precision={2} /></Col>
              <Col span={12}><Statistic title="Other" value={selectedDetail.other_deductions||0} prefix="₦" precision={2} /></Col>
            </Row>
            <Divider>Net Pay</Divider>
            <Card><Statistic title="Net Pay" value={selectedDetail.net_pay} prefix="₦" precision={2} valueStyle={{ color: '#52c41a', fontSize: '28px' }} /></Card>
            <div style={{ marginTop: 16, display: 'flex', gap: 12, alignItems: 'center' }}>
              <Tag color={selectedDetail.status==='paid'||selectedDetail.status==='processed'?'success':selectedDetail.status==='approved'?'processing':'warning'}>
                {selectedDetail.status?.toUpperCase()}
              </Tag>
              <Button icon={<DownloadOutlined />} type="primary" size="small"
                onClick={async () => { message.loading('Generating payslip PDF...', 1); await apiService.downloadPayslip(selectedDetail.id); }}>
                Download Payslip PDF
              </Button>
            </div>
          </div>
        )}
      </Drawer>

      {/* Create Modal */}
      <Modal title="Create New Payroll Record" open={createModalVisible} onCancel={() => { setCreateModalVisible(false); form.resetFields(); }} footer={null} width={800}>
        <Form form={form} layout="vertical" onFinish={handleCreateSubmit}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="employee_id" label="Employee" rules={[{ required: true }]}>
                <Select placeholder="Select employee" showSearch optionFilterProp="children">
                  {employees.map(e => <Select.Option key={e.id} value={e.id}>{e.first_name} {e.last_name} — {e.department}</Select.Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="pay_period" label="Pay Period" rules={[{ required: true }]}>
                <Input placeholder="e.g., June 2026" />
              </Form.Item>
            </Col>
          </Row>
          <EarningsFields formRef={form} />
          <Form.Item name="status" label="Status" initialValue="draft">
            <Select>
              <Select.Option value="draft">Draft</Select.Option>
              <Select.Option value="approved">Approved</Select.Option>
              <Select.Option value="processed">Processed</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">Create Payroll</Button>
              <Button onClick={() => { setCreateModalVisible(false); form.resetFields(); }}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Modal */}
      <Modal title="Edit Payroll Record" open={editModalVisible} onCancel={() => { setEditModalVisible(false); setSelectedRecord(null); form.resetFields(); }} footer={null} width={800}>
        <Form form={form} layout="vertical" onFinish={handleEditSubmit}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="employee_id" label="Employee" rules={[{ required: true }]}>
                <Select showSearch optionFilterProp="children">
                  {employees.map(e => <Select.Option key={e.id} value={e.id}>{e.first_name} {e.last_name} — {e.department}</Select.Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="pay_period" label="Pay Period" rules={[{ required: true }]}>
                <Input placeholder="e.g., June 2026" />
              </Form.Item>
            </Col>
          </Row>
          <EarningsFields formRef={form} />
          <Form.Item name="status" label="Status">
            <Select>
              <Select.Option value="draft">Draft</Select.Option>
              <Select.Option value="approved">Approved</Select.Option>
              <Select.Option value="processed">Processed</Select.Option>
              <Select.Option value="paid">Paid</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">Update Payroll</Button>
              <Button onClick={() => { setEditModalVisible(false); setSelectedRecord(null); form.resetFields(); }}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
      {/* Manual Payment Modal */}
      <Modal title={`Record Payment — ${manualPayRecord?.first_name || ''} ${manualPayRecord?.last_name || ''}`}
        open={manualPayModal} onCancel={() => { setManualPayModal(false); manualPayForm.resetFields(); }} footer={null} width={480}>
        <Alert message={`Net Pay: ₦${Number(manualPayRecord?.net_pay || 0).toLocaleString()}`} type="info" showIcon style={{ marginBottom: 16 }} />
        <Form form={manualPayForm} layout="vertical" onFinish={async (values) => {
          try {
            const tid = localStorage.getItem('hr_tenant_id') || 'superadmin';
            const r = await fetch(`https://nexus-hr.talenthubupskilling.com/backend/hr-api/payroll/payroll.php?tenant_id=${tid}&action=mark_paid`, {
              method: 'PUT', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: manualPayRecord?.id, payment_reference: values.reference, payment_note: values.note, tenant_id: tid })
            });
            const d = await r.json();
            if (d.success) {
              message.success('Payment recorded successfully');
              setManualPayModal(false); manualPayForm.resetFields(); fetchData();
            } else message.error(d.error || 'Failed to record payment');
          } catch { message.error('Failed to record payment'); }
        }}>
          <Form.Item name="reference" label="Payment Reference / Teller Number" rules={[{ required: paymentMethod !== 'cash' }]}>
            <Input placeholder={paymentMethod === 'cash' ? 'Optional reference' : 'e.g. TRF123456789 or Teller No.'} />
          </Form.Item>
          <Form.Item name="note" label="Note (Optional)">
            <Input.TextArea placeholder="e.g. Paid via GTBank transfer on 5 July 2026" rows={2} />
          </Form.Item>
          <Space>
            <Button type="primary" htmlType="submit">Mark as Paid & Send Payslip</Button>
            <Button onClick={() => setManualPayModal(false)}>Cancel</Button>
          </Space>
        </Form>
      </Modal>

      {/* Bank Transfer Setup Modal */}
      <Modal title={`Bank Details — ${bankEmployee?.first_name || ''} ${bankEmployee?.last_name || ''}`}
        open={bankModal} onCancel={() => { setBankModal(false); bankForm.resetFields(); setBankVerified(null); setRecipientCode(''); }} footer={null} width={560}>
        <Alert message="Set up employee bank account for salary transfer via Paystack." type="info" showIcon style={{ marginBottom: 16 }} />
        <Form form={bankForm} layout="vertical" onFinish={async (values) => {
          if (!bankEmployee) return;
          if (!bankVerified) {
            // Step 1: Verify account
            setVerifying(true);
            const verify = await apiService.verifyBankAccount(values.account_number, values.bank_code);
            setVerifying(false);
            if (!verify.status) { message.error('Account verification failed. Check account number and bank.'); return; }
            const accountName = verify.data?.account_name || values.account_name;
            bankForm.setFieldsValue({ account_name: accountName });
            setBankVerified({ ...values, account_name: accountName, bank_name: banks.find((b:any) => b.code === values.bank_code)?.name || '' });
            message.success(`✅ Verified: ${accountName}. Click "Save & Enable Transfer" to proceed.`);
          } else {
            // Step 2: Create recipient and save
            setVerifying(true);
            const res = await apiService.createTransferRecipient({
              employee_id: bankEmployee.id,
              account_number: bankVerified.account_number,
              bank_code: bankVerified.bank_code,
              bank_name: bankVerified.bank_name,
              account_name: bankVerified.account_name,
            });
            setVerifying(false);
            if (res.status) {
              setRecipientCode(res.data?.recipient_code || '');
              message.success(`✅ Bank details saved! You can now initiate transfers.`);
              setBankModal(false); bankForm.resetFields(); setBankVerified(null); fetchData();
            } else {
              message.error(res.message || res.error || 'Failed to save bank details');
            }
          }
        }}>
          <Form.Item name="bank_code" label="Bank" rules={[{ required: true }]}>
            <Select showSearch optionFilterProp="children" placeholder="Select bank"
              onDropdownVisibleChange={async (open) => {
                if (open && banks.length === 0) {
                  const r = await apiService.getBanks();
                  if (r.status) setBanks(r.data || []);
                }
              }}>
              {banks.map((b: any) => <Select.Option key={b.code} value={b.code}>{b.name}</Select.Option>)}
            </Select>
          </Form.Item>
          <Form.Item name="account_number" label="Account Number" rules={[{ required: true }, { len: 10, message: 'NUBAN is 10 digits' }]}>
            <Input maxLength={10} placeholder="0123456789" />
          </Form.Item>
          <Form.Item name="account_name" label="Account Name (auto-filled after verify)">
            <Input placeholder="Will be filled automatically" />
          </Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={verifying} icon={<BankOutlined />}>
              {bankVerified ? 'Save & Enable Transfer' : 'Verify Account'}
            </Button>
            {bankVerified && <Button onClick={() => setBankVerified(null)}>Re-verify</Button>}
            <Button onClick={() => setBankModal(false)}>Cancel</Button>
          </Space>
        </Form>
      </Modal>
    </div>
  );
};

export default PayrollDashboard;
